/**
 * Light MapLibre basemap on our Protomaps `niedersachsen.pmtiles` (Protomaps
 * Basemap v4 schema: earth · landuse · water · roads · buildings · places).
 * Ported from packages/transit-ui journeyMapStyle.ts and rethemed to TMN:
 * the basemap recedes in cool neutrals so the teal/lime route and pins lead.
 */
// a no-break space keeps „©“ on the line of the name it belongs to, however narrow the open credit
export const OSM_ATTRIBUTION = '©\u00a0<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>';

const GREEN_LANDUSE_KINDS = ['park', 'forest', 'wood', 'grass', 'meadow', 'recreation_ground', 'nature_reserve', 'cemetery', 'garden', 'village_green', 'golf_course', 'scrub', 'farmland', 'allotments'];
const PLACE_LABEL_KINDS = ['city', 'town', 'village', 'hamlet', 'suburb', 'neighbourhood', 'quarter', 'borough', 'locality'];
const ROAD_LABEL_KINDS = ['highway', 'major_road', 'medium_road'];
const FONT_REGULAR = ['Open Sans Regular'];
const FONT_ITALIC = ['Open Sans Italic'];

export function buildBasemapStyle(pmtilesUrl: string): maplibregl.StyleSpecification {
  const land = '#f4f6f5';
  const landAlt = '#e9edeb';
  const water = '#cfe3e0';
  const park = '#dbe9d3';
  const road = '#ffffff';
  const roadCasing = '#d5dbd8';
  const building = '#e1e6e3';
  const labelText = '#5b6663';
  const waterLabelText = '#0a6156';
  const labelHalo = '#ffffff';
  return {
    version: 8,
    glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
    sources: { nds: { type: 'vector', url: `pmtiles://${pmtilesUrl}`, attribution: OSM_ATTRIBUTION } },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': land } },
      { id: 'earth', type: 'fill', source: 'nds', 'source-layer': 'earth', paint: { 'fill-color': land } },
      { id: 'landuse', type: 'fill', source: 'nds', 'source-layer': 'landuse', paint: { 'fill-color': landAlt, 'fill-opacity': 0.6 } },
      { id: 'park', type: 'fill', source: 'nds', 'source-layer': 'landuse', filter: ['in', ['get', 'kind'], ['literal', GREEN_LANDUSE_KINDS]], paint: { 'fill-color': park, 'fill-opacity': 0.55 } },
      { id: 'water', type: 'fill', source: 'nds', 'source-layer': 'water', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': water } },
      {
        id: 'water-line', type: 'line', source: 'nds', 'source-layer': 'water', filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': water, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.6, 13, 1.4, 16, 3] },
      },
      { id: 'road-casing', type: 'line', source: 'nds', 'source-layer': 'roads', paint: { 'line-color': roadCasing, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 4] } },
      { id: 'road', type: 'line', source: 'nds', 'source-layer': 'roads', paint: { 'line-color': road, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 16, 2.5] } },
      { id: 'building', type: 'fill', source: 'nds', 'source-layer': 'buildings', minzoom: 14, paint: { 'fill-color': building, 'fill-opacity': 0.7 } },
      {
        id: 'water-label', type: 'symbol', source: 'nds', 'source-layer': 'water', minzoom: 11, filter: ['has', 'name'],
        layout: { 'text-field': ['get', 'name'], 'text-font': FONT_ITALIC, 'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 16, 14], 'text-max-width': 6, 'symbol-placement': 'point' },
        paint: { 'text-color': waterLabelText, 'text-halo-color': labelHalo, 'text-halo-width': 1.2, 'text-halo-blur': 0.4 },
      },
      {
        id: 'place-label', type: 'symbol', source: 'nds', 'source-layer': 'places', minzoom: 5,
        filter: ['all', ['has', 'name'], ['in', ['get', 'kind'], ['literal', PLACE_LABEL_KINDS]]],
        layout: { 'text-field': ['get', 'name'], 'text-font': FONT_REGULAR, 'text-size': ['interpolate', ['linear'], ['zoom'], 5, 10, 11, 11, 16, 14], 'text-max-width': 7, 'text-padding': 4 },
        paint: { 'text-color': labelText, 'text-halo-color': labelHalo, 'text-halo-width': 1.4, 'text-halo-blur': 0.4 },
      },
      {
        id: 'road-label', type: 'symbol', source: 'nds', 'source-layer': 'roads', minzoom: 13,
        filter: ['all', ['has', 'name'], ['in', ['get', 'kind'], ['literal', ROAD_LABEL_KINDS]]],
        layout: { 'symbol-placement': 'line', 'text-field': ['get', 'name'], 'text-font': FONT_REGULAR, 'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 16, 12], 'symbol-spacing': 350, 'text-max-angle': 30 },
        paint: { 'text-color': labelText, 'text-halo-color': labelHalo, 'text-halo-width': 1.2, 'text-halo-blur': 0.4 },
      },
    ],
  };
}
